import { ChangeEvent, useMemo, useState } from 'react'

import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { SelectRebrand } from 'src/components/atoms/SelectRebrand'
import TypographyRebrand from 'src/components/atoms/TypographyRebrand/TypographyRebrand'
import { PostsListingRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'
import { BlogPostCardRebrand } from 'src/components/molecules/BlogPostCardRebrand'
import { PaginationRebrand } from 'src/components/molecules/PaginationRebrand'

import { usePagination } from 'src/hooks/usePagination'
import { trpc } from 'src/utils/trpc'

const PostsListingSectionRebrand = ({
  heading,
  postsPerPage
}: PostsListingRebrandResult) => {
  const fallbackLimit = postsPerPage ?? 6
  const { currentPage, limit, offset, onPageClick, resetPagination } =
    usePagination({ limit: fallbackLimit })

  const [category, setCategory] = useState('all')
  const [year, setYear] = useState('all')

  const { data: categories } = trpc.useQuery(['sanity.getCategories'])
  const { data: posts, isLoading } = trpc.useQuery([
    'sanity.getPostsPaginated',
    { category, year, offset, limit }
  ])

  const categoryOptions = useMemo(() => {
    const options = (categories ?? []).map(c => ({
      label: c.title,
      value: c.slug.current
    }))
    options.unshift({ label: 'All categories', value: 'all' })
    return options
  }, [categories])

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const options: { label: string; value: string }[] = []
    for (let y = 2016; y <= currentYear; y += 1) {
      options.push({ label: y.toString(), value: y.toString() })
    }
    options.unshift({ label: 'All years', value: 'all' })
    return options
  }, [])

  const activeCategoryTitle =
    categories?.find(c => c.slug.current === category)?.title ?? 'All articles'

  return (
    <div className='container mx-auto px-4 lg:px-20'>
      <div className='mb-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <TypographyRebrand variant='h2' color='forestDeep'>
          <strong>{heading || activeCategoryTitle}</strong>
        </TypographyRebrand>
        <div className='flex flex-col gap-2 md:flex-row md:gap-2'>
          <SelectRebrand
            id='category'
            className='w-full md:w-[268px] bg-white!'
            options={categoryOptions}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              setCategory(e.target.value)
              resetPagination()
            }}
            value={category}
          />
          <SelectRebrand
            id='year'
            className='w-full md:w-[268px] bg-white!'
            options={yearOptions}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              setYear(e.target.value)
              resetPagination()
            }}
            value={year}
          />
        </div>
      </div>

      <LoadingSpinner isLoading={isLoading}>
        {!posts?.data?.length && (
          <TypographyRebrand variant='body' color='forestDeep'>
            No posts found
          </TypographyRebrand>
        )}
        <div className='grid gap-10 md:grid-cols-2 xl:grid-cols-3'>
          {posts?.data?.map(post => (
            <BlogPostCardRebrand key={post._id} post={post} />
          ))}
        </div>
      </LoadingSpinner>

      <PaginationRebrand
        count={posts?.count ?? 0}
        page={currentPage}
        pageSize={limit}
        onChange={onPageClick}
      />
    </div>
  )
}

export default PostsListingSectionRebrand
